import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IUser } from '../../lib/interfaces/IUser';

interface ITeamSlice {
  player1: IUser;
  player2: IUser;
  activePlayer: 'player1' | 'player2' | null;
  pauseRemainingTime: number;
  inGame: boolean;
}

const initialState: ITeamSlice = {
  player1: {} as IUser,
  player2: {} as IUser,
  activePlayer: null,
  pauseRemainingTime: 60,
  inGame: false,
};

const teamSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    setNames: (
      state: ITeamSlice,
      action: PayloadAction<{ player1Name: string; player2Name: string }>,
    ) => {
      state.player1.name = action.payload.player1Name;
      state.player2.name = action.payload.player2Name;
      state.player1.timerKey = `player1_${new Date().getTime()}`;
      state.player2.timerKey = `player2_${new Date().getTime()}`;
    },
    updateTimerKeys: (state: ITeamSlice, action: PayloadAction) => {
      state.player1.timerKey = `player1_${new Date().getTime()}`;
      state.player2.timerKey = `player2_${new Date().getTime()}`;
    },
    updateTimerPlayer1: (state: ITeamSlice, action: PayloadAction) => {
      state.player1.timerKey = `player1_${new Date().getTime()}`;
    },
    updateTimerPlayer2: (state: ITeamSlice, action: PayloadAction) => {
      state.player2.timerKey = `player2_${new Date().getTime()}`;
    },
    setColors: (
      state: ITeamSlice,
      action: PayloadAction<{ player1Color: 'blue' | 'red'; player2Color: 'blue' | 'red' }>,
    ) => {
      state.player1.teamColor = action.payload.player1Color;
      state.player2.teamColor = action.payload.player2Color;
    },
    setPlaying(state: ITeamSlice, action: PayloadAction<{ player: 'player1' | 'player2' }>) {
      const isPlayer1 = action.payload.player === 'player1';
      state.player1.isPlaying = isPlayer1;
      state.player2.isPlaying = !isPlayer1;
    },
    toggleUserPlaying(state: ITeamSlice, action: PayloadAction<{ player: 'player1' | 'player2' }>) {
      state[action.payload.player].isPlaying = !state[action.payload.player].isPlaying;
    },
    protest(state: ITeamSlice, action: PayloadAction) {
      state.player1.isPlaying = false;
      state.player2.isPlaying = false;
    },
    setActivePlayer(state: ITeamSlice, action: PayloadAction<{ player: 'player1' | 'player2' }>) {
      state.activePlayer = action.payload.player;
      state.inGame = true;
      state[action.payload.player].isPlaying = false;
    },
    setName(
      state: ITeamSlice,
      action: PayloadAction<{ player: 'player1' | 'player2'; name: string }>,
    ) {
      state[action.payload.player].name = action.payload.name;
    },
    setColor(
      state: ITeamSlice,
      action: PayloadAction<{ player: 'player1' | 'player2'; color: 'blue' | 'red' }>,
    ) {
      state[action.payload.player].teamColor = action.payload.color;
    },
    startGame(state: ITeamSlice, action: PayloadAction<{ player: 'player1' | 'player2' }>) {
      state.activePlayer = action.payload.player;
    },
    setPauseRemainingTime(
      state: ITeamSlice,
      action: PayloadAction<{ remainingTime: number; isAutoEnd?: boolean }>,
    ) {
      if (action.payload.isAutoEnd && action.payload.remainingTime <= 0) {
        state.activePlayer = null;
        state.pauseRemainingTime = 0;
      } else {
        state.pauseRemainingTime = action.payload.remainingTime;
      }
    },
    startPause(state: ITeamSlice, action: PayloadAction<{ player?: 'player1' | 'player2' }>) {
      if (action.payload.player) {
        state[action.payload.player].wasPause = true;
      }
      state.player1.isPlaying = false;
      state.player2.isPlaying = false;
    },
    stopPause(state: ITeamSlice, action: PayloadAction) {
      state.activePlayer = null;
    },
    setPlayerRemainingTime(
      state: ITeamSlice,
      action: PayloadAction<{ player: 'player1' | 'player2'; remainingTime: number }>,
    ) {
      const player = action.payload.player;
      state[player].remainingTime = action.payload.remainingTime;
    },
    completeGame(state: ITeamSlice, action: PayloadAction) {
      state.player1.wasPause = false;
      state.player2.wasPause = false;
    },
    resetTeam(state: ITeamSlice, action: PayloadAction) {
      state.player1 = {} as IUser;
      state.player2 = {} as IUser;
      state.activePlayer = null;
      state.inGame = false;
    },
    resetActivePlayer(state: ITeamSlice, action: PayloadAction) {
      state.activePlayer = null;
      state.player1.alreadyPlayed = false;
      state.player2.alreadyPlayed = false;
      state.player2.isPlaying = false;
      state.player1.isPlaying = false;
      state.player1.wasPause = false;
      state.player2.wasPause = false;
      state.inGame = false;
    },
    onCompleteGame(state: ITeamSlice, action: PayloadAction<{ player: 'player1' | 'player2' }>) {
      const player = action.payload.player;
      state[player].isPlaying = false;
      state[player].alreadyPlayed = true;
      state[player].remainingTime = 0;

      if (player === 'player2') {
        state.activePlayer = 'player1';
      } else {
        state.activePlayer = 'player2';
      }

      if (!state.player2.remainingTime && !state.player1.remainingTime) {
        state.activePlayer = null;
        state.player1.alreadyPlayed = false;
        state.player2.alreadyPlayed = false;
        state.player2.isPlaying = false;
        state.player1.isPlaying = false;
        state.player1.wasPause = false;
        state.player2.wasPause = false;
        state.inGame = false;
      }
    },
  },
});

export const teamActions = teamSlice.actions;

export default teamSlice.reducer;
